import React, {useEffect, useState} from "react";
import {Combination, MenuItem, MenuItemCombination} from "../../@types/stores/menu";
import {observer} from "mobx-react";
import "./style.scss";
import {getCombinationArticleOptionsUrl, getHost, getNextCombinationStep, getNextPackageArticleOptionsUrl} from "../../helpers/ApiHelper";
import defaultImg from "../../assets/images/default.webp";
import ModifiersGroup from "../MenuItem/ModifiersGroup";
import {OrderLineModifiers} from "../../@types/stores/order";
import lodash from "lodash";
import {useTranslation} from "react-i18next";
import orderStore from "../../stores/order";
import {useNavigate, useParams} from "react-router";
import {moveToTop} from "../../helpers/DomHelper";
import useLayoutStyles from "../../hooks/styles";


export interface CombinationArticleModifiersProps {
    combination: MenuItemCombination;
    step: Combination;
    item: MenuItem;
    menuId: number;
    subMenuId: number;
}

const CombinationArticleModifiers = ({item, combination, step, menuId, subMenuId}: CombinationArticleModifiersProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const params = useParams();
    const styles = useLayoutStyles();

    const [modifiers, setModifiers] = useState<OrderLineModifiers[]>([]);
    const [allowNext, setAllowNext] = useState<boolean>(true);

    useEffect(() => {
        checkAllowNext();
        if (modifiers.length) {
            const orderLine = orderStore.getCurrentCombinationArticleOrderLine(step, item);
            if (orderLine) {
                orderLine.modifiers = modifiers;
                orderStore.updateCurrentCombinationArticleOrderLine(combination, step, orderLine);
            }
        }
    }, [modifiers]);

    const checkAllowNext = () => {
        let allow = true;
        if (item.modifierGroups) {
            for (const group of item.modifierGroups) {
                const selectedModifierCount = modifiers.find(x => x.groupId === group.id)?.items.length || 0;
                const min = group.min || 0;
                if (selectedModifierCount < min)
                    allow = false;
            }
        }
        setAllowNext(allow);
    }

    const changeModifiers = (id: number, selectedModifiers: number[]) => {
        const _modifiers = lodash.cloneDeep(modifiers).filter((x: OrderLineModifiers) => x.groupId !== id);
        _modifiers.push({
            groupId: id,
            items: selectedModifiers
        });
        setModifiers(_modifiers);
    }

    const goNext = () => {
        if (!allowNext) return;

        const isChange = params.change === "change";

        moveToTop();

        if (combination.isPackage) {
            const nextArticleUrl = getCombinationArticleOptionsUrl(menuId, subMenuId, combination, step, item, isChange, "modifiers");
            navigate(nextArticleUrl || (isChange
                ? `/menu/combination/info`
                : getNextPackageArticleOptionsUrl(menuId, subMenuId, combination, step, item)));
            return;
        }

        if (item.ingredientGroups?.length) {
            navigate(`/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${step.id}/article/${item.id}/ingredients${isChange ? "/change" : ""}`);
            return;
        }

        moveToTop();

        const nextStep = getNextCombinationStep(combination, step);
        if (!isChange && nextStep)
            navigate(`/menu/combination/${menuId}/${subMenuId}/${combination.id}/step/${nextStep.id}`);
        else
            navigate(`/menu/combination/info`);
    }

    if (!item)
        return null;

    return <div className="CombinationArticleModifiers">
        <h2>{item.name}</h2>
        <div className="img"
             style={{backgroundImage: `url(${item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultImg)}`}}/>
        <div className="description">{item.description || ""}</div>
        <div className="modifiers">
            {item.modifierGroups?.length
                ? item.modifierGroups.map(mg => <ModifiersGroup key={`modifiersGroup_${mg.name}`} modifiersGroup={mg}
                                                                onChange={changeModifiers}
                                                                selected={modifiers.find(x => x.groupId === mg.id)?.items || []}/>)
                : null
            }
        </div>
        <div className={`button dark ${!allowNext ? "disable" : ""}`}
             onClick={goNext}>{t(modifiers.map(mg => mg.items).flat().length ? `next` : `skip`)}</div>
    </div>
}

export default observer(CombinationArticleModifiers);
